import React, { useEffect } from 'react';

import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useRouteError,
} from 'react-router';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

import { useAppSelector } from './reducers/hooks';

import { ROUTES } from './constants';

import { ParallaxProvider, config } from '@adc/parallax-component-library';

import MainLayout from './layouts/MainLayout';
import MinimalLayout from './layouts/MinimalLayout';

import LoginForm from './components/auth/LoginForm';
import ForgotPassword from './components/auth/ForgotPassword';

import Dashboard from './components/pages/Dashboard';
import Organizations from './components/pages/Organizations';
import Organization from './components/pages/Organization';
import Accounts from './components/pages/Accounts';
import { Account } from './components/pages/Account';
import RoleAssignments from './components/pages/RoleAssignments';
import Studies from './components/pages/Studies';
import Study from './components/pages/Study';
import Site from './components/pages/Site';
import Subject from './components/pages/Subject';
import Sensor from './components/pages/Sensor';
import About from './components/pages/About';

import UnexpectedError from './components/utility/UnexpectedError';
import NotFound from './components/utility/NotFound';
import VerifyAccount from './components/auth/VerifyAccount';
import ResetPassword from './components/auth/ResetPassword';
import ChangePassword from './components/auth/ChangePassword';

const queryClient = new QueryClient();

/**
 * Prevent the user from accessing protected routes if they are not logged in.
 * @returns {React.JSX.Element}
 */
export function ProtectedRoute() {
  const isLoggedIn = useAppSelector((state) => state.auth.loggedIn);

  useEffect(() => {
    if (!isLoggedIn) {
      queryClient.clear();
    }
  }, [isLoggedIn]);

  if (!isLoggedIn) {
    return <Navigate to={ROUTES.AUTH.LOGIN} replace />;
  }

  return <Outlet />;
}

/**
 * Prevent the user from accessing auth routes if they are already logged in.
 * @returns {React.JSX.Element}
 */
function AuthRoute() {
  const isLoggedIn = useAppSelector((state) => state.auth.loggedIn);

  if (isLoggedIn) {
    return <Navigate to={ROUTES.ROOT} replace />;
  }

  return <Outlet />;
}

function RouterErrorMessage() {
  const routeError = useRouteError();

  return <UnexpectedError errorMessage={String(routeError)} />;
}

const routes = [
  {
    path: ROUTES.AUTH.ROOT,
    element: <MinimalLayout />,
    errorElement: <RouterErrorMessage />,
    children: [
      {
        element: <AuthRoute />,
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.AUTH.LOGIN} replace />,
          },
          { path: ROUTES.AUTH.LOGIN, element: <LoginForm /> },
          {
            path: ROUTES.AUTH.FORGOT_PASSWORD,
            element: <ForgotPassword />,
          },
          {
            path: ROUTES.AUTH.VERIFY_ACCOUNT,
            element: <VerifyAccount />,
          },
          {
            path: ROUTES.AUTH.RESET_PASSWORD,
            element: <ResetPassword />,
          },
          {
            path: ROUTES.AUTH.CHANGE_PASSWORD,
            element: <ChangePassword />,
          },
        ],
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
  {
    path: ROUTES.ROOT,
    element: <ProtectedRoute />,
    errorElement: <RouterErrorMessage />,
    children: [
      {
        element: <MainLayout />,
        children: [
          {
            index: true,
            element: <Navigate to={ROUTES.DASHBOARD.ROOT} replace />,
          },
          { path: ROUTES.DASHBOARD.ROOT, element: <Dashboard /> },
          {
            path: ROUTES.ORGANIZATIONS.ROOT,
            children: [
              { index: true, element: <Organizations /> },
              { path: ROUTES.ORGANIZATIONS.DETAIL, element: <Organization /> },
            ],
          },
          {
            path: ROUTES.ACCOUNTS.ROOT,
            children: [
              { index: true, element: <Accounts /> },
              { path: ROUTES.ACCOUNTS.DETAIL, element: <Account /> },
            ],
          },
          {
            path: ROUTES.ROLE_ASSIGNMENTS.ROOT,
            children: [{ index: true, element: <RoleAssignments /> }],
          },
          {
            path: ROUTES.STUDIES.ROOT,
            children: [
              { index: true, element: <Studies /> },
              { path: ROUTES.STUDIES.DETAIL, element: <Study /> },
            ],
          },
          {
            path: ROUTES.SITES.ROOT,
            children: [
              { index: true, element: <Navigate to={ROUTES.ROOT} replace /> },
              { path: ROUTES.SITES.DETAIL, element: <Site /> },
            ],
          },
          {
            path: ROUTES.SUBJECTS.ROOT,
            children: [
              { index: true, element: <Navigate to={ROUTES.ROOT} replace /> },
              { path: ROUTES.SUBJECTS.DETAIL, element: <Subject /> },
            ],
          },
          {
            path: ROUTES.SENSORS.ROOT,
            children: [
              { index: true, element: <Navigate to={ROUTES.ROOT} replace /> },
              { path: ROUTES.SENSORS.DETAIL, element: <Sensor /> },
            ],
          },
          {
            path: '*',
            element: <NotFound />,
          },
        ],
      },
    ],
  },
  {
    path: ROUTES.SYSTEM.ABOUT,
    element: <MinimalLayout />,
    errorElement: <RouterErrorMessage />,
    children: [{ index: true, element: <About /> }],
  },
  { path: '*', element: <NotFound /> },
];

export const router = createBrowserRouter(routes);

/**
 * The main application component that sets up the routing and layout
 * @returns {React.JSX.Element}
 */
export function App() {
  return (
    <ParallaxProvider
      config={config}
      locale="en-US"
      dir="ltr"
      defaultTheme="light"
    >
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
        <ReactQueryDevtools />
      </QueryClientProvider>
    </ParallaxProvider>
  );
}

export default App;
